// Ported from `sntz_mockups/src/lib/design-tokens.ts` — subset needed for chat.
// Keep class strings: we spread these into `className` via cn().

export const TOUCH = {
    primary: "size-11 md:size-9",
    secondary: "size-11 md:size-8",
    tertiary: "size-9 md:size-7",
} as const;

export const SPACE = {
    pageX: "px-4",
    pageXWide: "px-4 lg:px-6",
    toolbar: "gap-1.5",
    tiles: "gap-2",
    section: "space-y-5",
    elements: "gap-2",
    msgY: "py-3",
    msgListX: "px-4",
    heroGutter: "w-full max-w-none px-4 md:px-6 xl:px-8 2xl:px-16 2xl:max-w-[1680px] 2xl:mx-auto",
    cardPad: "p-3 md:p-4",
    readCap: "max-w-[72ch] mx-auto",
} as const;

export const HEIGHT = {
    header: "h-12",
    input: "min-h-11 md:min-h-9",
    sendBtn: "h-11 md:h-10",
    tile: "min-h-11 md:min-h-9",
} as const;

export const HEADER = {
    height: "h-16",
    paddingX: "px-4 md:px-5",
    tabGap: "gap-1",
    actionGap: "gap-1.5",
} as const;

export const PROMPT = {
    outerX: "px-4",
    outerY: "pt-3 pb-3 md:pt-4 md:pb-5",
    innerPad: "p-4 md:p-5",
    toolbarPad: "px-2 py-2",
    toolbarGap: "gap-1.5",
    sendPad: "px-4 md:px-5",
} as const;

export const SETTINGS = {
    width: "w-80",
    padding: "p-4",
    sectionGap: "space-y-5",
    tilePad: "px-3 py-2 md:px-2.5 md:py-1.5",
} as const;

export const HERO = {
    gutter: "w-full max-w-none px-4 md:px-6 xl:px-8 2xl:px-16 2xl:max-w-[1680px] 2xl:mx-auto",
} as const;

export const BATCH_SIZES = [1, 2, 3, 4] as const;
export type BatchSize = (typeof BATCH_SIZES)[number];

// ═══ SIDEBAR_V5— V5 sidebar geometry & rules ═════════════════════════════
//
// Ported from `sntz_mockups/handoff/v5-sidebar/tokens/design-tokens.ts`.
// `SIDEBAR_V3` from the source is NOT ported — V5 is the only preset
// the SPA uses.
//
// Canonical doctrine — 9 design rules
// (sntz_mockups/handoff/v5-sidebar/docs/RULES.md):
//
//   Rule 1 — Two-rail alignment. Every visible right-edge element lands on
//     one of two vertical rails: OUTER (8 px from sidebar inner-right —
//     where interactive SURFACES end, i.e. button BG / row pill) and INNER
//     (16 px — where visible GLYPHS end, i.e. icon / chevron / trailing
//     text). See `outerRailFromInner` / `innerRailFromInner` below.
//   Rule 2 — Mobile-first touch targets. Every interactive surface is
//     ≥ 44 px tall on mobile (Apple HIG / WCAG 2.5.5 / Material 3) and
//     collapses to 32 px on desktop via `md:`. Pattern: `min-h-11 md:h-8`
//     for rows, `size-11 md:size-8` for square compact buttons.
//   Rule 3 — Motion-safe animations. Wrap rotation/translation/scale in
//     `motion-safe:` so `prefers-reduced-motion: reduce` users don't get
//     vestibular issues (WCAG 2.3.3).
//   Rule 4 — iOS safe-area inset. Footer must clear iPhone home-indicator:
//     `pb-[max(0.5rem,env(safe-area-inset-bottom))]`. Applied inline on
//     `SidebarFooter`, not via token.
//   Rule 5 — Font scale. Exactly three sizes in the sidebar: 11 px (eyebrows
//     — Chats / Library / Pinned / Today / see-all CTAs), 12 px (sub-labels
//     — workspace `Free`, wallet `$1,874`, NavUser `Enterprise`), 14 px
//     (every interactive row + every dropdown menu item). No stray `text-xs`
//     on row text.
//   Rule 6 — Focus-reveal parity. Hover-revealed buttons must also reveal
//     on `focus-within` so keyboard users can discover them on first tab.
//     Add `md:group-focus-within/row:opacity-100` alongside
//     `md:group-hover/row:opacity-100`.
//   Rule 7 — Right-of-trigger menu placement. Both project- and chat-row
//     context menus use `side="right" sideOffset={4} collisionPadding={8}`.
//     Radix auto-flips to `left` if there's no room.
//   Rule 8 — Section landmark naming. Every collapsible `<section>` has
//     `aria-label={label}` so screen readers expose it as a named region.
//   Rule 9 — No `mr-*` / `-mr-*` overrides without citation. Any negative
//     or right margin override must include a comment naming the rail
//     (`outerRailFromInner` / `innerRailFromInner`) it targets — otherwise
//     it's a review block.
//
// Tokens are Tailwind class strings spread into `className` via `cn()`.
// `outerRailFromInner` / `innerRailFromInner` are numeric so future rail-
// audit tests can measure real geometry against them.
export const SIDEBAR_V5 = {
    // Brand row (header) — sntz.ai wordmark + collapse trigger. `px-2`
    // produces an 8 px inner gutter matching every other row's content-x.
    brandRow: "flex items-center justify-between px-2 pt-3 pb-2",
    brand: "text-sm font-semibold tracking-tight",

    // Workspace pill. Rule 2: 44 px mobile, 32 px desktop.
    wsPill: "min-h-11 md:h-8 rounded-md px-2 gap-2 text-sm",
    // Rule 5: 14 px (text-sm) for the workspace name (sub-label inside row).
    wsName: "text-sm font-medium truncate",
    // Rule 5: 12 px sub-label for plan suffix ("Free" / "Team").
    wsPlan: "text-xs text-muted-foreground truncate",

    // Hero "+ New chat" row. Same geometry as `row`; emphasis comes from
    // `font-medium`, not from a chromatic gradient.
    newChatRow: "min-h-11 md:h-8 rounded-md px-2 gap-2 text-sm font-medium",

    // Generic interactive row — primary nav, project rows, chat rows.
    // Rule 2: 44 px mobile, 32 px desktop. Rule 5: text-sm = 14 px.
    row: "min-h-11 md:h-8 rounded-md px-2 gap-2 text-sm font-normal",
    // Active state — single neutral fill, no chromatic accent (matches
    // shadcn `data-[active=true]`).
    rowActive: "bg-sidebar-accent text-foreground",
    // Rule 5: 12 px (text-xs) for trailing badges inside a row (e.g. wallet
    // balance, count chip).
    trailingBadge: "ml-auto text-xs tabular-nums text-muted-foreground",

    // Section structure. `sectionContainer` provides 8 px outer padding;
    // `sectionHeader` has 8 px inner padding → eyebrow text lands at 16 px
    // from sidebar-left, matching every content row's text-x.
    sectionContainer: "px-2 py-1",
    sectionHeader: "flex items-center gap-1.5 h-8 px-2",
    // Rule 5: 11 px eyebrows (Chats / Library / Projects / Pinned / Today).
    // CSS uppercase — DOM keeps original case for screen readers.
    sectionLabel: "text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground",
    // Compact trailing eyebrow action button (e.g. PROJECTS "+"). Rule 1 +
    // Rule 2: `size-11 md:size-8` gives 44 px mobile / 32 px desktop;
    // `justify-end pr-2` right-aligns the visible glyph at the inner rail
    // (col 16) while the button BG sits at the outer rail (col 8).
    // Collapsed-mode overrides re-center the icon for icon-only sidebar.
    sectionAction:
        "ml-auto size-11 md:size-8 justify-end pr-2 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:!pr-0 text-muted-foreground hover:text-foreground",

    // Shared "see all" footer CTA — used by All projects / Show all pinned
    // / See all chats. Rule 2 (44/32 px) + Rule 5 (11 px = eyebrow tier).
    seeAllRow:
        "flex min-h-11 md:h-8 items-center justify-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground",
    seeAllCount: "tabular-nums text-muted-foreground/60",

    // NavUser footer pill.
    footerRow: "h-14 rounded-md px-2 gap-2",

    // Dropdown menu item baseline. Forces 44 px on mobile because shadcn's
    // default (`px-2 py-1.5 + text-sm`) renders ~32 px — fine for desktop,
    // fails Rule 2 on touch.
    menuItem: "min-h-11 md:min-h-8 gap-2 text-sm",

    // Rule 1 numeric constants — for future rail-audit tests that measure
    // real DOM geometry against these values.
    outerRailFromInner: 8,
    innerRailFromInner: 16,

    // Inter-row gap inside a cluster (4 px). Ensures uniform rhythm across
    // CHATS / LIBRARY / Projects / Pinned / date-group clusters.
    clusterGap: "space-y-0.5",
} as const;
